import {
  Account,
  ActivityFilterKey,
  ActivityFilterModel,
  ActivityInboxType,
  ActivityOrderBy,
  FeatureName,
  getActivityFilter,
  SortDirectionEnum,
  useActivityListFilter,
  useCurrentAccount,
  useCurrentWorkspace,
  useFindUnreadActivityInbox,
  useIsFeatureEnabled,
  useSetActivityListFilter,
} from '@gripp/shared-logic';
import { createColumnHelper } from '@tanstack/react-table';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, Text, View } from 'react-native';
import { useParams } from 'react-router-dom';
import { Colors } from '../../themes/colors';
import { InfiniteDataGrid } from '../infiniteDataGrid';
import { ActivityListOld } from './activityList-old';
import { ActivitySortDropdown } from './activitySortDropdown';
import { IssueCard, IssueCardData } from './issueCard';
import { IssueCardCompact } from './issueCardCompact';

export type ActivityListProps = {
  assetId?: string;
  showAssetName?: boolean;
  onIssueSelected?: (id: string) => void;
  onActivityCountChange?: (count: number) => void;
  selectedIssue?: string;
  showScrollIndicator?: boolean;
  containerStyle?: React.ComponentProps<typeof View>['style'];
  right?: React.ComponentType;
  filterKey: ActivityFilterKey;
  showTextEmpty?: boolean;
  excludeFilter?: boolean;
  onDataLoaded?: (data: any) => void;
  customEmptyState?: React.ReactNode;
  /**
   * Which card to render inside the list.
   * - default: full IssueCard (existing behavior)
   * - assetCompact: compact card for asset preview (title+icons+date+description)
   */
  cardVariant?: 'default' | 'assetCompact';
  /**
   * Page size used by the infinite grid query.
   */
  pageSize?: number;
  /**
   * Whether to show the list top bar (results + sort).
   */
  showTopBar?: boolean;
  paddingBottom?: number;
  /**
   * Whether to show the asset thumbnail image on cards (default: true).
   * Set to false when all activities belong to the same asset.
   */
  showImage?: boolean;
};

// Module-scope so the helper (and the `accessor(...)` column specs it produces)
// keep stable identities across renders. A per-render helper produces fresh
// column objects even when the `columns` useMemo skips — re-triggering the
// TanStack row re-mount + <Image> flash described in spec/0004_flickering_images.md.
const columnHelper = createColumnHelper<any>();

const getOrderByField = (orderBy: ActivityOrderBy): string => {
  if (orderBy === ActivityOrderBy.MostRecentUpdated) {
    return 'updatedAt';
  }
  return orderBy;
};

type ActivityCellContextValue = {
  cardVariant?: 'default' | 'assetCompact';
  showAssetName?: boolean;
  showImage?: boolean;
  account: Account | null;
  filter: ActivityFilterModel | undefined;
  selectedIssue?: string;
  hasUnread: (activity: IssueCardData) => boolean;
  isMentionedInInbox: (activity: IssueCardData) => boolean;
  onRegisterSwipeable: (id: string, ref: React.RefObject<any>) => void;
  onUnregisterSwipeable: (id: string) => void;
  onSwipeOpen: (id: string) => void;
  onSwipeClose: (id: string) => void;
};

const ActivityCellContext = createContext<ActivityCellContextValue | null>(
  null
);

// Module-scope component so its reference is stable across renders of the
// parent. `flexRender` renders this as `<ActivityMainCell row={...} />`; because
// the element type never changes, React reconciles cells in place when the
// context value updates instead of unmounting and remounting the row subtree
// (which would re-create every <Image> and cause the thumbnail flash).
const ActivityMainCell = ({ row }: { row: { original: IssueCardData } }) => {
  const ctx = useContext(ActivityCellContext);
  if (!ctx || !ctx.filter) return null;
  const activity = row.original;
  if (!activity) return null;
  const isSelected = activity.id === ctx.selectedIssue;
  if (ctx.cardVariant === 'assetCompact') {
    return (
      <IssueCardCompact
        account={ctx.account}
        activity={activity}
        showAssetName={ctx.showAssetName}
        isUnread={ctx.hasUnread(activity)}
        isMentionedInInbox={ctx.isMentionedInInbox(activity)}
        filter={ctx.filter}
        isSelected={isSelected}
        isCropCircleRedesignEnabled={true}
      />
    );
  }
  return (
    <IssueCard
      account={ctx.account}
      activity={activity}
      showAssetName={ctx.showAssetName}
      showImage={ctx.showImage}
      isUnread={ctx.hasUnread(activity)}
      isMentionedInInbox={ctx.isMentionedInInbox(activity)}
      filter={ctx.filter}
      isSelected={isSelected}
      isCropCircleRedesignEnabled={true}
      onRegisterSwipeable={ctx.onRegisterSwipeable}
      onUnregisterSwipeable={ctx.onUnregisterSwipeable}
      onSwipeOpen={ctx.onSwipeOpen}
      onSwipeClose={ctx.onSwipeClose}
    />
  );
};

// TODO: Crop Circle Redesign | remove once feature is fully rolled out
export const ActivityListBase = (props: ActivityListProps) => {
  const { data: filterData, isLoading: isLoadingFilter } =
    useActivityListFilter(props.filterKey);
  const { account } = useCurrentAccount();

  const { mutateAsync: setActivityListFilter } = useSetActivityListFilter(
    props.filterKey
  );

  const { t } = useTranslation();
  const [inbox, setInbox] = useState<any[]>([]);
  const [itemsCount, setItemsCount] = useState(0);
  const { issueId } = useParams();
  const selectedIssue = props.selectedIssue || issueId;
  const swipeableRegistryRef = useRef<Map<string, React.RefObject<any>>>(
    new Map()
  );
  const activeSwipeableIdRef = useRef<string | null>(null);
  const lastNavigationRef = useRef(0);

  const { data: inboxData } = useFindUnreadActivityInbox();

  const workspace = useCurrentWorkspace(true);

  const currentOrderBy =
    filterData?.orderBy || ActivityOrderBy.MostRecentUpdated;
  const orderByField = getOrderByField(currentOrderBy);

  useEffect(() => {
    if (inboxData) {
      setInbox(inboxData.items);
    }
  }, [inboxData]);

  const filter = useMemo(() => {
    if (!filterData || workspace === undefined) return undefined;

    // reset filtering only for Recent Activity
    const aFilter = props.excludeFilter
      ? new ActivityFilterModel()
      : filterData;

    return getActivityFilter(aFilter, workspace?.id, props.assetId);
  }, [filterData, workspace, props.assetId, props.excludeFilter]);

  const hasUnread = useCallback(
    (activity: any) => {
      return inbox.some(
        (i) =>
          (i.type === ActivityInboxType.IssueUpdated ||
            i.type === ActivityInboxType.IssueMessageCreated) &&
          i.data.id === activity.id
      );
    },
    [inbox]
  );

  const isMentionedInInbox = useCallback(
    (activity: any) => {
      const inboxItem = inbox.find(
        (i) =>
          i.type === ActivityInboxType.IssueMessageCreated &&
          i.data.id === activity.id
      );
      return inboxItem?.data?.isMentioned || false;
    },
    [inbox]
  );

  const rowStyle = (row: any) => {
    const isUnread = hasUnread(row.original);
    const isMentioned = isMentionedInInbox(row.original);

    if (props.cardVariant === 'assetCompact') {
      return styles.compactRow;
    }

    return [styles.row, isUnread && isMentioned ? styles.unread : styles.read];
  };

  const registerSwipeable = useCallback(
    (id: string, ref: React.RefObject<any>) => {
      swipeableRegistryRef.current.set(id, ref);
    },
    []
  );

  const unregisterSwipeable = useCallback((id: string) => {
    swipeableRegistryRef.current.delete(id);
  }, []);

  const closeOtherSwipeables = useCallback((currentId: string) => {
    const previousId = activeSwipeableIdRef.current;
    if (previousId && previousId !== currentId) {
      swipeableRegistryRef.current.get(previousId)?.current?.close?.();
    }
    activeSwipeableIdRef.current = currentId;
  }, []);

  const handleSwipeClose = useCallback((id: string) => {
    if (activeSwipeableIdRef.current === id) {
      activeSwipeableIdRef.current = null;
    }
  }, []);

  const columns: any[] = useMemo(() => {
    const cols: any[] = [
      columnHelper.accessor('id', {
        cell: ActivityMainCell,
        meta: {
          style: styles.cell,
          customRender: true,
        },
      }),
    ];

    if (props.right) {
      const RightComponent = props.right;
      // Inline closure is safe only while `props.right` is a stable reference
      // from the caller. If a caller ever passes an inline function, the cell
      // type will change between renders and re-trigger the row-remount flash
      // described in spec/0004_flickering_images.md — extract to a module-scope
      // component + context the same way ActivityMainCell does.
      cols.push(
        columnHelper.accessor('type', {
          cell: ({ row }) => {
            const activity = row.original as IssueCardData;
            return activity && (RightComponent as any)({ activity });
          },
          meta: {
            style: styles.right,
            customRender: true,
          },
        })
      );
    }

    return cols;
    // Deps deliberately narrowed to keep column element types stable across
    // renders — see spec/0004_flickering_images.md. Volatile state is passed
    // through ActivityCellContext instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.right]);

  const cellContextValue = useMemo<ActivityCellContextValue>(
    () => ({
      cardVariant: props.cardVariant,
      showAssetName: props.showAssetName,
      showImage: props.showImage,
      account,
      filter: filterData,
      selectedIssue,
      hasUnread,
      isMentionedInInbox,
      onRegisterSwipeable: registerSwipeable,
      onUnregisterSwipeable: unregisterSwipeable,
      onSwipeOpen: closeOtherSwipeables,
      onSwipeClose: handleSwipeClose,
    }),
    [
      props.cardVariant,
      props.showAssetName,
      props.showImage,
      account,
      filterData,
      selectedIssue,
      hasUnread,
      isMentionedInInbox,
      registerSwipeable,
      unregisterSwipeable,
      closeOtherSwipeables,
      handleSwipeClose,
    ]
  );

  const onActivitySelected = (activity: any) => {
    const now = Date.now();
    if (now - lastNavigationRef.current < 500) return;
    lastNavigationRef.current = now;
    props.onIssueSelected && props.onIssueSelected(activity.id);
  };

  const handleSortChange = async (newSort: ActivityOrderBy) => {
    if (filterData) {
      await setActivityListFilter(
        new ActivityFilterModel({
          ...filterData,
          orderBy: newSort,
        })
      );
    }
  };

  const onDataLoaded = (data: any[]) => {
    props.onDataLoaded?.(data);
  };

  if (isLoadingFilter) {
    return null;
  }

  return !filter ? null : (
    <ActivityCellContext.Provider value={cellContextValue}>
      <View
        style={[
          styles.container,
          props.cardVariant === 'assetCompact' && styles.compactContainer,
        ]}
      >
        {props.showTopBar !== false && (
          <View
            style={
              Platform.OS === 'web'
                ? styles.desktopStickyHeader
                : styles.mobileStickyHeader
            }
          >
            <Text style={styles.resultsText}>
              {itemsCount} {t('activity.topBar.results')}
            </Text>
            <ActivitySortDropdown
              selectedSort={currentOrderBy}
              onSortChange={handleSortChange}
            />
          </View>
        )}
        <View style={styles.listContainer}>
          <InfiniteDataGrid
            columns={columns}
            query={getActivities}
            dataPrefix="issue.findIssues"
            modelName="issue"
            rowTestIdPrefix="activity-row"
            onRowClick={onActivitySelected}
            orderBy={{
              field: orderByField,
              order: SortDirectionEnum.Desc,
            }}
            pageSize={props.pageSize ?? 50}
            filter={filter}
            hideHeader={true}
            hideResultsLabel={true}
            refetchOnFocus={true}
            showScrollIndicator={props.showScrollIndicator}
            containerStyle={[
              props.containerStyle,
              props.cardVariant === 'assetCompact' && {
                backgroundColor: Colors.brand.white,
              },
            ]}
            rowStyle={rowStyle}
            textEmpty={
              props.customEmptyState ||
              ((workspace || Platform.OS === 'web' || props.showTextEmpty) &&
                t('activity.textEmpty'))
            }
            onItemsCountChange={(count) => {
              setItemsCount(count);
              props.onActivityCountChange?.(count);
            }}
            enableScrollRefresh={true}
            onDataLoaded={onDataLoaded}
            disableHorizontalScroll={Platform.OS !== 'web'}
            paddingBottom={props.paddingBottom}
            disableRipple={
              Platform.OS !== 'web' || props.cardVariant === 'assetCompact'
            }
            disableHoverBg={props.cardVariant === 'assetCompact'}
          />
        </View>
      </View>
    </ActivityCellContext.Provider>
  );
};

export const ActivityList = (props: ActivityListProps) => {
  // TODO: Crop Circle Redesign | remove once feature is fully rolled out
  const isCropCircleEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  if (!isCropCircleEnabled) {
    return <ActivityListOld {...props} />;
  }

  return <ActivityListBase {...props} />;
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  listContainer: {
    flex: 1,
  },
  compactContainer: {
    // No background color - let parent handle it
  },
  desktopStickyHeader: {
    zIndex: 1000,
    backgroundColor: Colors.brand.white,
    borderBottomWidth: 1,
    borderTopWidth: 1,
    borderColor: Colors.secondaryGrayHeader,
    paddingHorizontal: 16,
    paddingVertical: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 50,
  },
  mobileStickyHeader: {
    zIndex: 1000,
    backgroundColor: Colors.brand.white,
    borderBottomWidth: 1,
    borderTopWidth: 1,
    borderColor: Colors.secondaryGrayHeader,
    paddingHorizontal: 16,
    paddingVertical: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 50,
  },
  resultsText: {
    fontSize: 16,
    fontWeight: '500',
    color: Colors.grayText,
    letterSpacing: -0.25,
  },
  cell: {
    alignSelf: 'stretch',
    alignContent: 'stretch',
    width: '100%',
  },
  row: {
    paddingLeft: 0,
    paddingRight: 0,
    borderBottomWidth: 1,
    borderBottomColor: Colors.secondaryGrayHeader,
    height: 'auto',
    minHeight: 100,
  },
  compactRow: {
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 2,
    paddingBottom: 2,
    borderBottomWidth: 0,
    height: 'auto',
    minHeight: 0,
    backgroundColor: Colors.brand.white,
  },
  right: {
    maxWidth: 5,
  },
  read: {
    backgroundColor: Colors.brand.white,
  },
  unread: {
    backgroundColor: Colors.brand.white,
  },
  emptyContainer: {
    flexDirection: 'column',
    rowGap: 40,
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
    width: '100%',
  },
  textWrapper: {
    width: '100%',
  },
  emptyTapText: {
    fontWeight: '700',
    fontSize: 22,
    textAlign: 'center',
  },
});

const getActivities = `
    query FindIssues($filter: FindIssuesFilter, $page: PageRequest, $orderBy: OrderByInput) {
      issue {
        findIssues(filter: $filter, page: $page, orderBy: $orderBy) {
          count
          offset
          limit
          items {
            id
            severity
            type
            title
            subtitle
            isPinned
            asset {
              name
              coverImage {
                id
                url
                extension
                status
                type
              }
            }
            workspace {
              id
              name
            }
            labels {
              id
              name
            }
            createdAt
            effectiveCreationDate
            createdBy {
              id
              name {
                first
                last
              }
            }
            updatedAt
            updatedBy {
              id
              name {
                first
                last
              }
            }
            nextReminderDate
            userMentions {
              id
              name {
                first
                last
              }
            }
            latestMessage {
              text
              i18n
              createdBy {
                id
                name {
                  first
                  last
                }
              }
              userMentions {
                id
                name {
                  first
                  last
                }
              }
              reactions {
                id
                createdAt
                createdBy
                reactionType
              }
              type
            }
          }
        }
      }
    }
  `;
